/**
 * Contacts: who to reach and how (Customer 360, 02 §5.6). The account's own
 * contact as TallyPrime or the sheet gives it — name, number, email, address —
 * then the other people the team keeps in Timely (0033), in that order, which
 * is the order Call rings. Timely names no "primary" contact and stores no
 * preference: the order is the records' own. Then who works the account, and
 * the ledger's details, read-only.
 */
import { buttonClass, IconMail, IconPhone, IconUser, Panel, cx } from '@timely/ui';
import { RANK_WORDS, dialableNumber, firstDialable } from '@timely/domain';
import { useFormatters } from '../../book/useFormatters';
import { AssignCollector } from '../../book/AssignCollector';
import { PaymentRankControl } from '../../book/PaymentRankControl';
import { ContactPeople } from '../../book/ContactPeople';
import { isTallyKey } from '../../book/accountCode';
import { useActiveWorkspace } from '../../workspace/WorkspaceProvider';
import { useAccount } from './accountPage';

export function CustomerContacts() {
    const page = useAccount();
    const { workspace } = useActiveWorkspace();
    const { customer, rights } = page;
    const from = page.source === 'tally' ? 'TallyPrime' : 'your receivables sheet';
    const first = firstDialable(customer);
    return (
        <div className="space-y-6">
            <Panel
                headingLevel={3} title="People to reach"
                description={!rights.contact ? 'The account’s own contact, then the other people the team keeps.'
                    : first ? 'Call rings the first number here, in this order.'
                        : 'Nobody here has a number Call can ring.'}
            >
                <div className="px-5 pb-4">
                    <OwnContact from={from} />
                    <ContactPeople
                        customer={customer}
                        workspaceId={workspace.id}
                        updatedAt={page.updatedAt}
                        canEdit={rights.edit}
                        canContact={rights.contact}
                        disabled={page.busy}
                        onSaved={page.changed}
                        onError={page.failed}
                    />
                </div>
            </Panel>
            <div className="grid gap-6 lg:grid-cols-2 items-start">
                <WhoWorksIt />
                <AccountDetails from={from} />
            </div>
        </div>
    );
}

/**
 * The contact the books carry: the one the source gives, said to come from there. Call and Email for whoever may work
 * the account; the number and the address, as text, for everyone who may read it.
 */
function OwnContact({ from }: { from: string }) {
    const { customer, rights } = useAccount();
    const dial = customer.contactNumber && rights.contact ? dialableNumber(customer.contactNumber) : null;
    const mail = rights.contact ? customer.email : null;
    // The address in full, with its city and state under it — a TallyPrime ledger has a state and no city, so
    // neither is named as the other.
    const address = [customer.address, [customer.city, customer.state].filter(Boolean).join(', ')].filter(Boolean).join('\n');
    const any = Boolean(customer.contactPerson || customer.contactNumber || customer.email || address);
    return (
        <section aria-label={`The contact in ${from}`}>
            <h4 className="text-secondary font-semibold text-label mb-1">
                The account’s contact <span className="font-normal text-label-3">· from {from}</span>
            </h4>
            {!any ? (
                <p className="text-secondary text-label-3 py-2">{from === 'TallyPrime' ? 'The ledger in TallyPrime gives no contact.' : 'The sheet gives no contact.'}</p>
            ) : (
                <div className="py-2 grid gap-x-6 gap-y-3 sm:grid-cols-[minmax(0,1fr)_auto] items-start">
                    <div className="min-w-0 flex items-start gap-2.5">
                        <span className="text-label-3 mt-0.5 flex-none" aria-hidden="true"><IconUser size={14} /></span>
                        <div className="min-w-0">
                            <p className="text-secondary font-semibold text-label break-words">
                                {customer.contactPerson || 'No name given'}
                                {customer.contactPost && <span className="font-normal text-label-3"> · {customer.contactPost}</span>}
                            </p>
                            {/* Whole, never cut: a TallyPrime phone field can hold several numbers. */}
                            {customer.contactNumber && (
                                <p className="text-secondary text-label-2 num wrap-anywhere">
                                    {dial ? <a href={`tel:${dial}`} className="text-accent-text font-semibold hover:underline">{customer.contactNumber}</a> : customer.contactNumber}
                                    {!dial && rights.contact && <span className="text-caption text-label-3"> · not a number Call can ring</span>}
                                </p>
                            )}
                            {customer.email && (
                                <p className="text-secondary text-label-2 wrap-anywhere">
                                    {mail ? <a href={`mailto:${mail}`} className="text-accent-text font-semibold hover:underline">{mail}</a> : customer.email}
                                </p>
                            )}
                            {address && <p className="mt-1 text-secondary text-label-2 whitespace-pre-line wrap-anywhere">{address}</p>}
                        </div>
                    </div>
                    {(dial || mail) && (
                        <div className="flex gap-2 flex-wrap sm:justify-end">
                            {dial && <a href={`tel:${dial}`} className={buttonClass({ variant: 'secondary', size: 'sm' })}><IconPhone size={14} />Call</a>}
                            {mail && <a href={`mailto:${mail}`} className={buttonClass({ variant: 'secondary', size: 'sm' })}><IconMail size={14} />Email</a>}
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}

/** Who works the account, and how it pays: the controls for whoever may change them, the facts for everyone else. */
function WhoWorksIt() {
    const page = useAccount();
    const { workspace } = useActiveWorkspace();
    const { customer, rights, names, row } = page;
    const collector = customer.collectorMemberId ? names.get(customer.collectorMemberId as string) : undefined;
    const owner = customer.ownerMemberId ? names.get(customer.ownerMemberId as string) : undefined;
    return (
        <Panel headingLevel={3} title="Who works it" description="Kept in Timely, never in the books.">
            <div className="px-5 pb-5 space-y-3">
                <Detail label="Owner" value={owner || 'Nobody'} />
                {rights.assign ? (
                    <AssignCollector
                        customer={customer}
                        workspaceId={workspace.id}
                        disabled={page.busy}
                        onError={page.failed}
                        onAssigned={name => page.changed(name ? `${name} now works this account.` : 'The owner works this account again.')}
                    />
                ) : (
                    <Detail label="Collector" value={collector || (owner ? `None — ${owner}, the owner, works it` : 'None')} />
                )}
                {rights.edit ? (
                    <PaymentRankControl
                        customer={customer}
                        workspaceId={workspace.id}
                        updatedAt={page.updatedAt}
                        disabled={page.busy}
                        onChanged={page.changed}
                        onError={page.failed}
                    />
                ) : (
                    <Detail label="Payment rank" value={`${RANK_WORDS[row.rank]}${customer.paymentRank ? '' : ' (automatic)'}`} />
                )}
            </div>
        </Panel>
    );
}

/** The ledger's own details. Read-only, and said so; how old the money is and the bills are the Bills tab's. */
function AccountDetails({ from }: { from: string }) {
    const { customer } = useAccount();
    const { money, count } = useFormatters();
    const tally = isTallyKey(customer.sourceKey);
    return (
        <Panel headingLevel={3} title="Account details" description={`From ${from}. Read-only: Timely never changes them.`}>
            <div className="px-5 pb-5">
                {tally
                    ? <Detail label="TallyPrime ledger ID" value={customer.sourceKey?.slice('tally:'.length)} mono />
                    : <Detail label="Customer code" value={customer.sourceKey} mono />}
                <Detail label="Tax identifier" value={customer.taxId} mono />
                <Detail label="Category" value={customer.category} />
                <Detail label="Credit limit" value={customer.creditLimit ? money.exact(customer.creditLimit) : ''} mono />
                <Detail label="Payment terms" value={customer.paymentTermsDays ? `${count(customer.paymentTermsDays)} days` : ''} />
                {/* The code a sheet carried. A sync never acts on it; matching it to a member is Settings › Account owners.
                    A TallyPrime ledger carries none, so a TallyPrime account is not asked. */}
                {!tally && <Detail label="Owner code in the sheet" value={customer.importedOwnerCode} mono />}
            </div>
        </Panel>
    );
}

/** One detail: its label, and the value whole — wrapped, never cut to one line. */
const Detail = ({ label, value, mono }: { label: string; value?: string | null | undefined; mono?: boolean }) => (
    <div className="flex items-baseline justify-between gap-4 py-1.5 border-b border-separator last:border-b-0">
        <span className="text-secondary text-label-3 flex-none">{label}</span>
        <span className={cx('text-secondary text-right min-w-0 wrap-anywhere', value ? 'text-label' : 'text-label-3', mono && 'num')}>{value || '—'}</span>
    </div>
);
